import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errText, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { resetFeatureDefaultsCache } from "./FeatureMenu";
import { CategoryChanger } from "./CategoryChanger";
import { CATEGORY_LABEL } from "../lib/constants";
import { Flag, Trash2, Check, ExternalLink, Clock, MessageSquare, X, Loader2, Send, Users, UserCog, Plus, Inbox, Star, Pin, RotateCcw, RotateCw, Eye, MousePointerClick, Ban, AlertTriangle, ShieldAlert, TrendingUp, BarChart3, MoreVertical, Archive, Eraser } from "lucide-react";

export const REJECT_PRESETS = [
  "Prohibited/restricted animal",
  "Poor or missing photos",
  "Suspected scam/fraud",
  "Duplicate listing",
  "Insufficient details",
  "Inappropriate content",
  "Wrong category",
];

const DIFF_FIELD_LABELS = {
  title: "Title", description: "Description", category: "Category", species: "Species",
  breed: "Breed", age: "Age", price: "Price", price_type: "Price type",
  province: "Province", city: "City", show_email: "Show email", show_phone: "Show phone",
  contact_phone: "Phone", photos: "Photos",
};

const fmtVal = (f, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (f === "photos") return `${v} photo${v === 1 ? "" : "s"}`;
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
};

// Field-by-field before→after diff for a pending (unapproved) edit.
const EditDiff = ({ listing, pe }) => {
  const fields = ["title", "price", "price_type", "description", "category", "species",
    "breed", "age", "province", "city", "show_email", "show_phone", "contact_phone"];
  const rows = [];
  for (const f of fields) {
    if (!(f in pe)) continue;
    if (pe[f] !== listing[f]) rows.push({ field: f, from: listing[f], to: pe[f] });
  }
  const oldPhotos = listing.photos || [];
  if (pe.photos && JSON.stringify(pe.photos) !== JSON.stringify(oldPhotos)) {
    rows.push({ field: "photos", from: oldPhotos.length, to: pe.photos.length });
  }
  if (!rows.length) return <p className="text-xs text-slate-500">No field changes detected.</p>;
  return (
    <div className="space-y-1.5" data-testid="edit-diff">
      {rows.map((r) => (
        <div key={r.field} className="text-xs flex flex-wrap items-baseline gap-x-1.5" data-testid={`edit-diff-${r.field}`}>
          <span className="font-semibold text-slate-500 min-w-[72px]">{DIFF_FIELD_LABELS[r.field] || r.field}:</span>
          <span className="line-through text-slate-400">{fmtVal(r.field, r.from)}</span>
          <span className="text-slate-400">→</span>
          <strong className="text-[#12234A]">{fmtVal(r.field, r.to)}</strong>
        </div>
      ))}
    </div>
  );
};

// Collapsible log of every past edit decision on a listing.
const EditHistory = ({ history }) => {
  const [open, setOpen] = useState(false);
  if (!history || !history.length) return null;
  const items = [...history].reverse();
  return (
    <div className="mt-2" data-testid="edit-history">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="edit-history-toggle"
        className="text-[11px] font-semibold text-slate-500 hover:text-[#12234A] inline-flex items-center gap-1">
        <Clock size={12} /> Edit history ({history.length}) {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="mt-1.5 space-y-2 border-l-2 border-slate-200 pl-3">
          {items.map((h, i) => (
            <div key={i} className="text-[11px]" data-testid={`edit-history-entry-${i}`}>
              <p className="font-semibold text-slate-600">
                <span className={h.decision === "approved" ? "text-emerald-600" : "text-red-600"}>
                  {h.decision === "approved" ? "Approved" : "Rejected"}
                </span>
                {" by "}{h.by || "staff"}{" · "}{h.at ? new Date(h.at).toLocaleString() : ""}
              </p>
              {h.reason && <p className="text-slate-500">Reason: {h.reason}</p>}
              {(h.changes || []).map((c, ci) => (
                <p key={ci} className="text-slate-500">
                  <span className="font-medium">{DIFF_FIELD_LABELS[c.field] || c.field}:</span>{" "}
                  <span className="line-through text-slate-400">{fmtVal(c.field, c.from)}</span> → <strong className="text-slate-600">{fmtVal(c.field, c.to)}</strong>
                </p>
              ))}
              {!(h.changes || []).length && <p className="text-slate-400 italic">No field changes recorded.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const PendingQueue = () => {
  const [data, setData] = useState(null);
  const [msgFor, setMsgFor] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulking, setBulking] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // {type:'single', l} | {type:'bulk', ids}
  const [rejectReason, setRejectReason] = useState("");
  const load = () => api.get("/staff/pending").then((r) => { setData(r.data); setSelected([]); }).catch((e) => toast.error(errText(e)));
  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return; // pause polling while tab hidden
      api.get("/staff/pending").then((r) => setData(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const approve = async (id) => {
    try { await api.post(`/staff/listings/${id}/approve`); toast.success("Approved & now live"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const approveEdit = async (id) => {
    try { await api.post(`/staff/listings/${id}/approve-edit`); toast.success("Edit approved & now live"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const openReject = (target) => { setRejectTarget(target); setRejectReason(""); };
  const confirmReject = async () => {
    const reason = rejectReason.trim();
    try {
      if (rejectTarget.type === "bulk") {
        const { data: res } = await api.post("/staff/listings/bulk", { ids: rejectTarget.ids, action: "reject", reason });
        toast.success(`${res.processed} ad${res.processed === 1 ? "" : "s"} rejected`);
      } else if (rejectTarget.type === "edit") {
        await api.post(`/staff/listings/${rejectTarget.l.id}/reject-edit`, { reason });
        toast.success("Edit rejected — previous version stays live");
      } else {
        await api.post(`/staff/listings/${rejectTarget.l.id}/reject`, { reason });
        toast.success("Listing rejected");
      }
      setRejectTarget(null); setRejectReason(""); load();
    } catch (e) { toast.error(errText(e)); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectableIds = () => (data?.pending || []).filter((l) => l.review_type !== "edit").map((l) => l.id);
  const toggleAll = () => setSelected((s) => s.length === selectableIds().length ? [] : selectableIds());

  const bulk = async (action) => {
    if (!selected.length) return;
    if (action === "reject") { openReject({ type: "bulk", ids: selected }); return; }
    setBulking(true);
    try {
      const { data: res } = await api.post("/staff/listings/bulk", { ids: selected, action });
      toast.success(`${res.processed} ad${res.processed === 1 ? "" : "s"} approved`);
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setBulking(false); }
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  if (!data.pending.length) return (
    <div className="text-center py-20 bg-white rounded-2xl border border-slate-200"><Check size={40} className="mx-auto text-emerald-400" /><p className="text-slate-500 mt-3">No ads awaiting approval. 🎉</p></div>
  );

  const allChecked = selectableIds().length > 0 && selected.length === selectableIds().length;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-[#12234A]">
          <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="bulk-select-all" />
          {selected.length ? `${selected.length} selected` : "Select all"}
        </label>
        <div className="flex gap-2 ml-auto">
          <Button onClick={() => bulk("approve")} disabled={!selected.length || bulking} data-testid="bulk-approve" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-9 disabled:opacity-40">
            {bulking ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Approve selected
          </Button>
          <Button onClick={() => bulk("reject")} disabled={!selected.length || bulking} variant="outline" data-testid="bulk-reject" className="rounded-xl text-red-600 gap-1.5 h-9 disabled:opacity-40">
            <X size={15} /> Reject selected
          </Button>
        </div>
      </div>
      {data.pending.map((l) => {
        const isEdit = l.review_type === "edit";
        const pe = l.pending_edit || {};
        return (
        <div key={l.id} className={`bg-white rounded-2xl border p-4 flex flex-col sm:flex-row gap-4 transition-colors ${isEdit ? "border-amber-300 bg-amber-50/40" : selected.includes(l.id) ? "border-[#F5551E] bg-[#FFF7F4]" : "border-slate-200"}`} data-testid={`pending-${l.id}`}>
          <div className="flex items-start gap-3 shrink-0">
            {!isEdit && <Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`bulk-check-${l.id}`} className="mt-1" />}
            <img src={photoUrl((isEdit ? (pe.photos || []) : (l.photos || []))[0] || (l.photos || [])[0])} alt="" className="h-24 w-32 rounded-xl object-cover bg-slate-100" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isEdit
                ? <Badge className="bg-amber-500 text-white" data-testid={`edit-badge-${l.id}`}>Edited · re-approval</Badge>
                : <Badge className="bg-amber-500 text-white">Pending</Badge>}
              <span className="text-xs text-slate-400">by {l.seller_name}</span>
            </div>
            <Link to={`/listing/${l.id}`} className="mt-1 inline-flex items-center gap-1 font-semibold text-[#12234A] hover:text-[#F5551E]">{l.title} <ExternalLink size={14} /></Link>
            {isEdit ? (
              <div className="mt-2 rounded-xl border border-amber-200 bg-white p-3" data-testid={`edit-changes-${l.id}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1.5">Proposed changes (live version stays until approved)</p>
                <EditDiff listing={l} pe={pe} />
                <EditHistory history={l.edit_history} />
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600 line-clamp-2 mt-1">{l.description || "No description"}</p>
                <p className="text-xs text-slate-400 mt-1">{CATEGORY_LABEL[l.category] || l.category} · {l.city}, {l.province}</p>
                <div className="mt-2"><CategoryChanger listing={l} onChange={(c) => setData((d) => ({ ...d, pending: d.pending.map((x) => (x.id === l.id ? { ...x, category: c } : x)) }))} /></div>
                <EditHistory history={l.edit_history} />
              </>
            )}
          </div>
          <div className="flex sm:flex-col gap-2 shrink-0">
            {isEdit ? (
              <>
                <Button onClick={() => approveEdit(l.id)} data-testid={`approve-edit-${l.id}`} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"><Check size={15} /> Approve edit</Button>
                <Button onClick={() => openReject({ type: "edit", l })} variant="outline" data-testid={`reject-edit-${l.id}`} className="rounded-xl text-red-600 gap-1.5"><X size={15} /> Reject edit</Button>
              </>
            ) : (
              <>
                <Button onClick={() => approve(l.id)} data-testid={`approve-${l.id}`} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"><Check size={15} /> Approve</Button>
                <Button onClick={() => openReject({ type: "single", l })} variant="outline" data-testid={`reject-${l.id}`} className="rounded-xl text-red-600 gap-1.5"><X size={15} /> Reject</Button>
              </>
            )}
            <Button onClick={() => setMsgFor(l)} variant="outline" data-testid={`message-poster-${l.id}`} className="rounded-xl gap-1.5"><MessageSquare size={15} /> Message</Button>
          </div>
        </div>
        );
      })}
      <MessagePosterDialog listing={msgFor} onClose={() => setMsgFor(null)} />
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><X size={18} className="text-red-600" /> {rejectTarget?.type === "edit" ? "Reject edit" : `Reject listing${rejectTarget?.type === "bulk" ? "s" : ""}`}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {rejectTarget?.type === "bulk"
                ? `Rejecting ${rejectTarget?.ids?.length} selected ad(s). Pick a reason — it's shared with the poster.`
                : rejectTarget?.type === "edit"
                  ? <>Rejecting the proposed edits to “{rejectTarget?.l?.title}”. The previously approved version stays live. Pick a reason — it's shared with the poster.</>
                  : <>Rejecting “{rejectTarget?.l?.title}”. Pick a reason — it's shared with the poster.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2" data-testid="reject-presets">
            {REJECT_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setRejectReason(p)}
                data-testid={`reject-preset-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${rejectReason === p ? "bg-[#F5551E] border-[#F5551E] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E]"}`}
              >
                {p}
              </button>
            ))}
          </div>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="reject-reason"
            rows={3}
            maxLength={500}
            placeholder="Add a custom note (optional) — or tap a preset above."
            className="rounded-xl resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={confirmReject} data-testid="reject-confirm" className="rounded-xl bg-red-600 hover:bg-red-700 text-white gap-1.5"><X size={15} /> Reject{rejectTarget?.type === "bulk" ? ` ${rejectTarget?.ids?.length}` : ""}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function MessagePosterDialog({ listing, onClose }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) return toast.error("Write a message first");
    setSending(true);
    try {
      const { data } = await api.post(`/staff/listings/${listing.id}/message`, { message: text });
      toast.success(data.email_sent ? "Message sent — poster notified by email" : "Message sent to poster");
      setText("");
      onClose();
    } catch (e) { toast.error(errText(e)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="message-poster-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><MessageSquare size={18} className="text-[#F5551E]" /> Message the poster</DialogTitle>
          <DialogDescription className="text-slate-500">
            Ask the poster for more info about “{listing?.title}”. They'll get your message in their inbox and by email.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="staff-message-text" rows={4} className="rounded-xl" placeholder="e.g. Hi! Could you add proof of vaccination before we approve this ad?" />
        <Button onClick={send} disabled={sending} data-testid="send-staff-message-btn" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
          {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />} Send message
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export const ReportInsights = ({ version, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/reports/insights").then((r) => setData(r.data)).catch(() => setData({ leaderboard: [], flagged: [], threshold: 3 }));
  useEffect(() => { load(); }, [version]);

  const suspend = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/admin/users/${uid}/status`, { status: "suspended" });
      toast.success("User suspended & their active ads hidden");
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(null); }
  };

  if (!data) return <Skeleton className="h-24 rounded-2xl" />;
  const { leaderboard = [], flagged = [], threshold = 3 } = data;
  if (!leaderboard.length) return null;
  const maxOpen = Math.max(1, ...leaderboard.map((r) => r.open_count));

  return (
    <div className="space-y-3" data-testid="report-insights">
      {flagged.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="auto-suspend-warning">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-red-600" />
            <p className="font-display font-bold text-red-700">Repeat-offender alert</p>
            <span className="ml-auto text-xs font-semibold text-red-600 bg-white/70 rounded-full px-2 py-0.5">{flagged.length} flagged</span>
          </div>
          <p className="text-sm text-red-600/90 mt-1">These users have {threshold}+ open reports. Review and suspend before more buyers are harmed.</p>
          <div className="mt-3 space-y-2">
            {flagged.map((f) => (
              <div key={f.user_id} className="flex items-center gap-3 bg-white rounded-xl border border-red-100 px-3 py-2" data-testid={`flagged-user-${f.user_id}`}>
                <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={photoUrl(f.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{f.name?.[0]}</AvatarFallback></Avatar>
                <Link to={`/seller/${f.user_id}`} className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-[#12234A] truncate hover:text-[#F5551E]">{f.name}</p>
                  <p className="text-xs text-red-600 font-medium">{f.open_count} open · {f.total_count} total reports</p>
                </Link>
                {isAdmin ? (
                  <Button onClick={() => suspend(f.user_id)} disabled={busy === f.user_id} data-testid={`suspend-flagged-${f.user_id}`}
                    className="rounded-xl h-9 gap-1.5 bg-red-600 hover:bg-red-700 text-white shrink-0">
                    {busy === f.user_id ? <Loader2 className="animate-spin" size={15} /> : <Ban size={15} />} Suspend
                  </Button>
                ) : (
                  <span className="text-xs text-slate-400 shrink-0">Admin can suspend</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="report-leaderboard">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={18} className="text-[#F5551E]" />
          <h3 className="font-display text-lg font-bold text-[#12234A]">Most-reported users</h3>
          <span className="ml-auto text-xs text-slate-400">Open reports first</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">Includes ad, message &amp; profile reports attributed to each user</p>
        <div className="space-y-1.5">
          {leaderboard.map((r, i) => (
            <div key={r.user_id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-slate-50 transition-colors" data-testid={`leaderboard-row-${r.user_id}`}>
              <span className="text-sm font-bold text-slate-400 w-5 text-center">{i + 1}</span>
              <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={photoUrl(r.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{r.name?.[0]}</AvatarFallback></Avatar>
              <Link to={`/seller/${r.user_id}`} className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[#12234A] truncate hover:text-[#F5551E] flex items-center gap-1.5">
                  {r.name}
                  {r.role !== "user" && <span className="text-[9px] bg-[#12234A] text-white px-1.5 py-0.5 rounded-full uppercase">{r.role}</span>}
                  {r.status === "suspended" && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full uppercase">suspended</span>}
                </p>
                <p className="text-xs text-slate-400 truncate">{r.top_reasons.map(([reason, n]) => `${reason} (${n})`).join(" · ")}</p>
              </Link>
              <div className="hidden sm:block w-24 shrink-0">
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{ width: `${(r.open_count / maxOpen) * 100}%` }} /></div>
              </div>
              <div className="flex items-center gap-3 text-xs font-medium shrink-0">
                <span className={`inline-flex items-center gap-1 ${r.open_count >= 3 ? "text-red-600" : "text-slate-600"}`}><Flag size={12} /> {r.open_count} open</span>
                <span className="text-slate-400 w-10 text-right">{r.total_count} all</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ReportsQueue = () => {
  const [data, setData] = useState(null);
  const [convFor, setConvFor] = useState(null); // {reportId, messageId, name}
  const [convMsgs, setConvMsgs] = useState(null);
  const [insightsKey, setInsightsKey] = useState(0);
  const load = () => api.get("/admin/reports").then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`, { action });
      toast.success(action === "remove" ? "Report resolved" : "Report dismissed");
      load();
      setInsightsKey((k) => k + 1);
    } catch (e) { toast.error(errText(e)); }
  };

  const openConversation = async (r) => {
    setConvFor({ reportId: r.id, messageId: r.message_id, name: r.reported_user_name });
    setConvMsgs(null);
    try {
      const { data: res } = await api.get(`/staff/conversations/${r.conversation_id}/messages`);
      setConvMsgs(res.messages || []);
    } catch (e) { toast.error(errText(e)); setConvMsgs([]); }
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;

  return (
    <div className="space-y-3">
      <ReportInsights version={insightsKey} onChanged={() => { load(); setInsightsKey((k) => k + 1); }} />
      {data.reports.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200"><Flag size={40} className="mx-auto text-slate-300" /><p className="text-slate-500 mt-3">No reports. All clear! 🐾</p></div>
      ) : data.reports.map((r) => {
        const isMsg = r.type === "message";
        const isUser = r.type === "user";
        return (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`report-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isMsg && <Badge className="bg-[#12234A] text-white gap-1"><MessageSquare size={11} /> Message report</Badge>}
                {isUser && <Badge className="bg-amber-600 text-white gap-1"><UserCog size={11} /> User report</Badge>}
                <Badge variant="outline" className="border-red-200 text-red-600 bg-red-50">{r.reason}</Badge>
                {r.status === "open" ? <Badge className="bg-amber-500 text-white">Open</Badge> : <Badge className="bg-emerald-600 text-white">Resolved · {r.resolution}</Badge>}
                {!isMsg && !isUser && (r.listing_status === "removed" || !r.listing_exists) && <Badge variant="outline" className="text-slate-500">Listing removed</Badge>}
              </div>
              {isMsg ? (
                <div className="mt-2">
                  <p className="font-semibold text-[#12234A]">Message from {r.reported_user_name}</p>
                  <p className="text-sm text-slate-600 mt-1 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 italic">"{r.message_body || "(message was deleted)"}"</p>
                  {r.conversation_id && (
                    <button onClick={() => openConversation(r)} data-testid={`view-conversation-${r.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] hover:text-[#E04812]"><MessageSquare size={14} /> View full conversation</button>
                  )}
                </div>
              ) : isUser ? (
                <Link to={`/seller/${r.reported_user_id}`} className="mt-2 inline-flex items-center gap-1 font-semibold text-[#12234A] hover:text-[#F5551E]">{r.reported_user_name} <ExternalLink size={14} /></Link>
              ) : (
                <Link to={`/listing/${r.listing_id}`} className="mt-2 inline-flex items-center gap-1 font-semibold text-[#12234A] hover:text-[#F5551E]">{r.listing_title} <ExternalLink size={14} /></Link>
              )}
              {r.details && <p className="text-sm text-slate-600 mt-1">Note: "{r.details}"</p>}
              <p className="text-xs text-slate-400 mt-2">Reported by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}{r.resolved_by && ` · resolved by ${r.resolved_by}`}</p>
            </div>
            {r.status === "open" && (
              <div className="flex gap-2 shrink-0">
                <Button onClick={() => resolve(r.id, "dismiss")} variant="outline" data-testid={`dismiss-${r.id}`} className="rounded-xl gap-1.5"><Check size={15} /> Dismiss</Button>
                <Button onClick={() => resolve(r.id, "remove")} data-testid={`remove-report-${r.id}`} className="rounded-xl gap-1.5 bg-red-600 hover:bg-red-700 text-white"><Trash2 size={15} /> {isMsg ? "Delete message" : isUser ? "Mark reviewed" : "Remove ad"}</Button>
              </div>
            )}
          </div>
        </div>
        );
      })}

      <Dialog open={!!convFor} onOpenChange={(o) => !o && setConvFor(null)}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="conversation-context-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><MessageSquare size={18} className="text-[#F5551E]" /> Reported conversation</DialogTitle>
            <DialogDescription className="text-slate-500">Full thread in context. The reported message is highlighted.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1" data-testid="conversation-context-thread">
            {!convMsgs ? (
              <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" size={26} /></div>
            ) : convMsgs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No messages found.</p>
            ) : convMsgs.map((m) => {
              const flagged = m.id === convFor?.messageId;
              return (
                <div key={m.id} className={`rounded-xl border px-3 py-2 ${flagged ? "border-amber-300 bg-amber-50" : "border-slate-100 bg-slate-50"}`} data-testid={`context-message-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[#12234A] flex items-center gap-1">{m.sender_name}{m.sender_is_staff && <span className="text-[9px] bg-[#12234A] text-white px-1.5 py-0.5 rounded-full uppercase">Staff</span>}</span>
                    <span className="text-[10px] text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className={`text-sm mt-1 ${m.deleted ? "italic text-slate-400" : "text-slate-700"}`}>{m.deleted ? "This message was deleted" : m.body}{m.edited && <span className="ml-1 text-[10px] text-slate-400">(edited)</span>}</p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ListingsModeration = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [reinstateFor, setReinstateFor] = useState(null);
  const [reinstateNote, setReinstateNote] = useState("");
  const [delTarget, setDelTarget] = useState(null); // {type:'single', l} | {type:'bulk', ids}
  const [delReason, setDelReason] = useState("");
  const [delNotify, setDelNotify] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reasons, setReasons] = useState(null);
  const [defaultDays, setDefaultDays] = useState(null);
  const load = () => api.get(`/staff/listings?q=${encodeURIComponent(q)}`).then((r) => { setItems(r.data); setSelected([]); }).catch((e) => toast.error(errText(e)));
  const loadReasons = () => { if (isAdmin) api.get("/admin/listings/deletion-reasons").then((r) => setReasons(r.data)).catch(() => {}); };
  const loadDefaultDays = () => { if (isAdmin) api.get("/admin/listing-expiry").then((r) => setDefaultDays(r.data.days)).catch(() => {}); };
  useEffect(() => {
    load();
    loadReasons();
    loadDefaultDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDefaultDays = async (days) => {
    try {
      const { data } = await api.put("/admin/listing-expiry", { days });
      setDefaultDays(data.days);
      toast.success(data.days === 0 ? "New listings will never expire" : `New listings now last ${data.days} days`);
    } catch (e) { toast.error(errText(e)); }
  };

  const bulkSetExpiry = async (days) => {
    if (!selected.length) return;
    try {
      const { data } = await api.post("/staff/listings/set-expiry", { ids: selected, days });
      toast.success(`${data.updated} listing${data.updated === 1 ? "" : "s"} set to ${days === 0 ? "never expire" : days + " days"}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/staff/listings/${id}/status`, { status }); toast.success(status === "removed" ? "Listing hidden" : "Listing restored"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const showUndoToast = (undo, count) => {
    const ids = (undo || []).map((u) => u.id);
    toast.success(count > 1 ? `${count} listings deleted` : "Listing deleted", {
      description: delNotify ? "The owner has been notified." : undefined,
      duration: 30000,
      action: ids.length ? {
        label: "Undo",
        onClick: async () => {
          try { await api.post("/staff/listings/undo-delete", { ids }); toast.success("Restored"); load(); loadReasons(); }
          catch (e) { toast.error(errText(e)); }
        },
      } : undefined,
    });
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      let res;
      if (delTarget.type === "bulk") {
        res = await api.post("/staff/listings/bulk-delete", { ids: delTarget.ids, notify: delNotify, reason: delReason.trim() });
      } else {
        res = await api.delete(`/staff/listings/${delTarget.l.id}`, { data: { notify: delNotify, reason: delReason.trim() } });
      }
      const count = res.data.processed ?? 1;
      showUndoToast(res.data.undo, count);
      setDelTarget(null); setDelReason(""); setDelNotify(true);
      load(); loadReasons();
    } catch (e) { toast.error(errText(e)); }
    finally { setDeleting(false); }
  };

  const openDelete = (target) => { setDelTarget(target); setDelReason(""); setDelNotify(true); };

  const doReinstate = async () => {
    try {
      await api.post(`/staff/listings/${reinstateFor.id}/status`, { status: "active", note: reinstateNote.trim() });
      toast.success("Listing reinstated — the moderator has been notified");
      setReinstateFor(null); setReinstateNote("");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const doFeature = async (l, days, autoRenew = false) => {
    try {
      const r = await api.post(`/staff/listings/${l.id}/feature`, { featured: true, days: days || null, auto_renew: autoRenew });
      const rotated = r.data?.rotated_off || [];
      const span = days ? ` for ${days} days${autoRenew ? " (auto-renew)" : ""}` : "";
      toast.success(rotated.length ? `Featured${span} · auto-rotated ${rotated.length} older (max ${r.data.max_featured})` : `Added to featured${span}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const doUnfeature = async (l) => {
    try {
      await api.post(`/staff/listings/${l.id}/feature`, { featured: false });
      toast.success("Removed from featured");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const doPin = async (l, days, autoRenew = false) => {
    try {
      const r = await api.post(`/staff/listings/${l.id}/pin`, { pinned: true, days: days || null, auto_renew: autoRenew });
      const rotated = r.data?.rotated_off || [];
      const span = days ? ` for ${days} days${autoRenew ? " (auto-renew)" : ""}` : "";
      toast.success(rotated.length ? `Pinned${span} · auto-unpinned ${rotated.length} older (max ${r.data.max_pinned})` : `Pinned to top${span}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const doUnpin = async (l) => {
    try {
      await api.post(`/staff/listings/${l.id}/pin`, { pinned: false });
      toast.success("Unpinned from top");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allIds = (items || []).map((l) => l.id);
  const toggleAll = () => setSelected((s) => s.length === allIds.length ? [] : allIds);
  const allChecked = allIds.length > 0 && selected.length === allIds.length;
  const delCount = delTarget?.type === "bulk" ? delTarget.ids.length : 1;
  const topReason = reasons?.reasons?.[0];
  const maxReason = topReason?.count || 1;

  return (
    <div>
      {isAdmin && reasons?.total > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4" data-testid="deletion-reasons-report">
          <h3 className="font-display text-sm font-bold text-[#12234A] flex items-center gap-1.5 mb-3">
            <BarChart3 size={15} className="text-red-500" /> Why ads get deleted
            <span className="text-xs font-medium text-slate-400">· {reasons.total} total</span>
          </h3>
          <div className="space-y-2">
            {reasons.reasons.map((r) => (
              <div key={r.reason} className="flex items-center gap-3" data-testid={`reason-row-${r.reason.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <span className="text-xs text-slate-600 w-44 shrink-0 truncate">{r.reason}</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-400 rounded-full" style={{ width: `${Math.max(6, (r.count / maxReason) * 100)}%` }} />
                </div>
                <span className="text-xs font-semibold text-[#12234A] w-8 text-right">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="mod-listing-search" placeholder="Search by title or advertiser…" className="flex-1 h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-[#F5551E]" />
        <Button onClick={load} className="rounded-xl bg-[#12234A] text-white">Search</Button>
      </div>

      {isAdmin && defaultDays !== null && (
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="listing-default-expiry-bar">
          <span className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5"><Clock size={15} className="text-[#F5551E]" /> New-listing lifespan</span>
          <span className="text-xs text-slate-500">How long a new post stays live before it expires.</span>
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 ml-auto" data-testid="listing-default-expiry-options">
            {[30, 60, 90, 0].map((d) => (
              <button key={d} onClick={() => saveDefaultDays(d)} data-testid={`listing-default-expiry-${d}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${defaultDays === d ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500 hover:text-[#12234A]"}`}>
                {d === 0 ? "Permanent" : `${d} days`}
              </button>
            ))}
          </div>
        </div>
      )}

      {isAdmin && <FeatureDefaultsBar />}

      {isAdmin && items && items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="listings-bulk-bar">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#12234A] cursor-pointer">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="listings-select-all" />
            {selected.length ? `${selected.length} selected` : "Select all"}
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={!selected.length} variant="outline" data-testid="bulk-set-expiry" className="rounded-xl gap-1.5 h-9 disabled:opacity-40">
                <Clock size={15} /> Set expiry
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuLabel className="text-xs">Set expiration for {selected.length} selected</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => bulkSetExpiry(30)} data-testid="bulk-expiry-30">Expire in 30 days</DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetExpiry(60)} data-testid="bulk-expiry-60">Expire in 60 days</DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetExpiry(90)} data-testid="bulk-expiry-90">Expire in 90 days</DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetExpiry(0)} data-testid="bulk-expiry-permanent" className="text-emerald-600 font-semibold">Never expire (permanent)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => openDelete({ type: "bulk", ids: selected })} disabled={!selected.length} data-testid="bulk-delete" className="rounded-xl bg-red-600 hover:bg-red-700 text-white gap-1.5 h-9 disabled:opacity-40 ml-auto">
            <Trash2 size={15} /> Delete selected
          </Button>
        </div>
      )}

      {!items ? <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div> : (
        <div className="space-y-2">
          {items.map((l) => (
            <div key={l.id} className={`bg-white rounded-xl border p-3 flex items-center gap-3 ${selected.includes(l.id) ? "border-[#F5551E] bg-[#FFF7F4]" : (l.featured || l.pinned) ? "border-[#F5551E]" : "border-slate-200"}`} data-testid={`mod-listing-${l.id}`}>
              {isAdmin && <Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`listing-check-${l.id}`} />}
              <div className="flex-1 min-w-0">
                <Link to={`/listing/${l.id}`} className="font-semibold text-[#12234A] hover:text-[#F5551E] truncate flex items-center gap-1.5">
                  {l.featured && <Star size={13} className="fill-[#F5551E] text-[#F5551E] shrink-0" data-testid={`featured-icon-${l.id}`} />}
                  {l.pinned && <Pin size={13} className="fill-[#12234A] text-[#12234A] shrink-0" data-testid={`pinned-icon-${l.id}`} />}
                  <span className="truncate">{l.title}</span>
                </Link>
                <p className="text-xs text-slate-400">{l.seller_name} · {l.city}, {l.province}</p>
                <div className="mt-1.5"><CategoryChanger listing={l} onChange={(c) => setItems((xs) => xs.map((x) => (x.id === l.id ? { ...x, category: c } : x)))} /></div>
                {l.status === "active" && (
                  <p className="text-[11px] mt-0.5 inline-flex items-center gap-1" data-testid={`listing-expiry-${l.id}`}>
                    <Clock size={11} className="text-slate-400" />
                    {l.expires_at
                      ? <span className="text-slate-400">expires {l.expires_at.slice(0, 10)}</span>
                      : <span className="text-emerald-600 font-semibold">never expires</span>}
                  </p>
                )}
                {(l.featured || l.featured_views > 0 || l.featured_clicks > 0) && (
                  <p className="mt-1 inline-flex items-center gap-3 text-xs text-[#F5551E] font-medium" data-testid={`featured-stats-${l.id}`}>
                    <span className="inline-flex items-center gap-1"><Eye size={12} /> {l.featured_views || 0} views</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick size={12} /> {l.featured_clicks || 0} clicks</span>
                    <span className="text-slate-400">{l.featured ? "· while featured" : "· last featured"}</span>
                  </p>
                )}
              </div>
              <Badge className={l.status === "active" ? "bg-emerald-600 text-white" : l.status === "rejected" ? "bg-red-500 text-white" : "bg-slate-400 text-white"}>{l.status}</Badge>
              {l.status === "active" && (
                <>
                  {l.featured ? (
                    <Button onClick={() => doUnfeature(l)} variant="outline" size="sm" data-testid={`feature-${l.id}`} className="rounded-lg gap-1 text-[#F5551E] border-[#F5551E]">
                      <Star size={13} className="fill-[#F5551E]" /> Unfeature{l.featured_until ? <span className="text-[10px] font-normal text-slate-400">· til {l.featured_until.slice(0, 10)}</span> : ""}{l.feature_renew_days ? <RotateCw size={11} className="text-[#F5551E]" data-testid={`feature-renew-icon-${l.id}`} /> : null}
                    </Button>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`feature-${l.id}`} className="rounded-lg gap-1 text-slate-600">
                          <Star size={13} /> Feature
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-xs">Add to featured carousel</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => doFeature(l, null)} data-testid={`feature-indefinite-${l.id}`}>Feature indefinitely</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doFeature(l, 7)} data-testid={`feature-7-${l.id}`}>Feature for 7 days</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doFeature(l, 14)} data-testid={`feature-14-${l.id}`}>Feature for 14 days</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doFeature(l, 30)} data-testid={`feature-30-${l.id}`}>Feature for 30 days</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs flex items-center gap-1"><RotateCw size={11} /> Auto-renew on lapse</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => doFeature(l, 7, true)} data-testid={`feature-7-renew-${l.id}`}>7 days (auto-renew)</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doFeature(l, 14, true)} data-testid={`feature-14-renew-${l.id}`}>14 days (auto-renew)</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doFeature(l, 30, true)} data-testid={`feature-30-renew-${l.id}`}>30 days (auto-renew)</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {l.pinned ? (
                    <Button onClick={() => doUnpin(l)} variant="outline" size="sm" data-testid={`pin-${l.id}`} className="rounded-lg gap-1 text-[#12234A] border-[#12234A]">
                      <Pin size={13} className="fill-[#12234A]" /> Unpin{l.pinned_until ? <span className="text-[10px] font-normal text-slate-400">· til {l.pinned_until.slice(0, 10)}</span> : ""}{l.pin_renew_days ? <RotateCw size={11} className="text-[#12234A]" data-testid={`pin-renew-icon-${l.id}`} /> : null}
                    </Button>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`pin-${l.id}`} className="rounded-lg gap-1 text-slate-600">
                          <Pin size={13} /> Pin
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel className="text-xs">Pin to top of results</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => doPin(l, null)} data-testid={`pin-indefinite-${l.id}`}>Pin indefinitely</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doPin(l, 7)} data-testid={`pin-7-${l.id}`}>Pin for 7 days</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doPin(l, 14)} data-testid={`pin-14-${l.id}`}>Pin for 14 days</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doPin(l, 30)} data-testid={`pin-30-${l.id}`}>Pin for 30 days</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs flex items-center gap-1"><RotateCw size={11} /> Auto-renew on lapse</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => doPin(l, 7, true)} data-testid={`pin-7-renew-${l.id}`}>7 days (auto-renew)</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doPin(l, 14, true)} data-testid={`pin-14-renew-${l.id}`}>14 days (auto-renew)</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => doPin(l, 30, true)} data-testid={`pin-30-renew-${l.id}`}>30 days (auto-renew)</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
              {l.status === "active" && (
                <Button onClick={() => setStatus(l.id, "removed")} variant="outline" size="sm" data-testid={`hide-${l.id}`} className="rounded-lg text-red-600">Hide</Button>
              )}
              {l.status === "removed" && (
                <Button onClick={() => setStatus(l.id, "active")} variant="outline" size="sm" data-testid={`restore-${l.id}`} className="rounded-lg text-emerald-600">Restore</Button>
              )}
              {l.status === "rejected" && (
                isAdmin
                  ? <Button onClick={() => { setReinstateFor(l); setReinstateNote(""); }} variant="outline" size="sm" data-testid={`reinstate-${l.id}`} className="rounded-lg text-emerald-600 gap-1"><RotateCcw size={13} /> Reinstate</Button>
                  : <span className="text-xs text-slate-400 italic px-2" data-testid={`reinstate-locked-${l.id}`}>Admin only</span>
              )}
              {isAdmin && (
                <Button onClick={() => openDelete({ type: "single", l })} variant="outline" size="sm" data-testid={`delete-${l.id}`} className="rounded-lg text-red-600 border-red-200 hover:bg-red-50 gap-1">
                  <Trash2 size={13} /> Delete
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!reinstateFor} onOpenChange={(o) => !o && setReinstateFor(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="reinstate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><RotateCcw size={18} className="text-emerald-600" /> Reinstate listing</DialogTitle>
            <DialogDescription className="text-slate-500">
              Reinstating “{reinstateFor?.title}”. Add a short note for {reinstateFor?.reviewed_by || "the moderator"} explaining why you're overriding the rejection.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reinstateNote}
            onChange={(e) => setReinstateNote(e.target.value)}
            data-testid="reinstate-note"
            rows={4}
            maxLength={500}
            placeholder="e.g. This looks legitimate on review — the photos meet our guidelines."
            className="rounded-xl resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setReinstateFor(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={doReinstate} data-testid="reinstate-confirm" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"><RotateCcw size={15} /> Reinstate & notify</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="delete-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Delete {delCount > 1 ? `${delCount} listings` : "listing"} permanently</DialogTitle>
            <DialogDescription className="text-slate-500">
              {delTarget?.type === "bulk"
                ? `This permanently removes ${delCount} selected listing(s) from the site.`
                : `This permanently removes “${delTarget?.l?.title}” from the site.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700" data-testid="delete-warning">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Unlike “Hide”, deleting cannot be reversed from the list — but you'll get a 30-second Undo right after.</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REJECT_PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setDelReason(p)} data-testid={`delete-preset-${p.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${delReason === p ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#F5551E]"}`}>
                {p}
              </button>
            ))}
          </div>
          <Textarea
            value={delReason}
            onChange={(e) => setDelReason(e.target.value)}
            data-testid="delete-reason"
            rows={3}
            maxLength={500}
            placeholder={delCount > 1 ? "Shared reason for all selected (shown to owners if you notify)…" : "Reason for removal (shown to the owner if you notify them)…"}
            className="rounded-xl resize-none"
          />
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="delete-notify-label">
            <Checkbox checked={delNotify} onCheckedChange={(v) => setDelNotify(!!v)} data-testid="delete-notify-toggle" />
            <span className="text-sm text-slate-700">Notify {delCount > 1 ? "owners" : "the owner"} by in-site message and email</span>
          </label>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting} data-testid="delete-confirm" className="rounded-xl bg-red-600 hover:bg-red-700 text-white gap-1.5">
              {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete{delCount > 1 ? ` ${delCount}` : ""}{delNotify ? " & notify" : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ---- Shared staff conversations (Users / Moderators split) ----
export const useStaffUnread = () => {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () => api.get("/conversations")
      .then((r) => { if (active) setUnread(r.data.reduce((n, c) => n + (c.muted ? 0 : (c.unread || 0)), 0)); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);
  return unread;
};

function ConvList({ items, activeId, onSelect, onDelete, emptyText }) {
  if (!items.length) return <div className="p-8 text-center text-sm text-slate-400" data-testid="conv-empty">{emptyText}</div>;
  return items.map((c) => (
    <div key={c.id} role="button" tabIndex={0} onClick={() => onSelect(c.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(c.id); } }}
      data-testid={`admin-conv-${c.id}`}
      className={`group relative w-full text-left p-4 pr-10 border-b border-slate-100 flex gap-3 hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none focus:bg-slate-50 ${activeId === c.id ? "bg-[#FFF4F0]" : ""}`}>
      <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={photoUrl(c.other_user.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{c.other_user.name?.[0]}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1">
          <p className="font-semibold text-[#12234A] text-sm truncate flex items-center gap-1">
            {c.other_user.name}
            {(c.other_role === "moderator" || c.other_role === "admin") && <span className="text-[9px] bg-[#12234A] text-white px-1.5 py-0.5 rounded-full uppercase">{c.other_role}</span>}
          </p>
          {c.unread > 0 && <span className="bg-[#F5551E] text-white text-[10px] rounded-full h-5 min-w-5 px-1 flex items-center justify-center">{c.unread}</span>}
        </div>
        <p className="text-xs text-slate-400 truncate">{c.kind === "direct" ? "Direct message" : c.listing_title}</p>
        <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || "No messages yet"}</p>
      </div>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(c.id); }} data-testid={`conv-delete-${c.id}`} title="Remove from my inbox"
          className="absolute top-1/2 -translate-y-1/2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-300 hover:text-red-500 transition-all p-1.5 rounded-md hover:bg-red-50">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  ));
}

export const StaffConversations = () => {
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [team, setTeam] = useState([]);
  const [segment, setSegment] = useState("users"); // "users" | "team"
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null); // "wipe" | "archive"
  const [confirmRemove, setConfirmRemove] = useState(null); // conversation id to remove from inbox
  const [selMsgs, setSelMsgs] = useState([]);
  const [confirmMulti, setConfirmMulti] = useState(false);
  const toggleMsgSel = (id) => setSelMsgs((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const loadConvs = useCallback(() => api.get("/conversations").then((r) => setConvs(r.data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { loadConvs(); api.get("/staff/team").then((r) => setTeam(r.data)).catch(() => {}); }, [loadConvs]);

  const isTeam = (c) => c.other_role === "moderator" || c.other_role === "admin";
  const userConvs = (convs || []).filter((c) => !isTeam(c));
  const teamConvs = (convs || []).filter(isTeam);
  const list = segment === "team" ? teamConvs : userConvs;

  const loadThread = useCallback(() => {
    if (!activeId) { setThread(null); return; }
    setSelMsgs([]);
    api.get(`/conversations/${activeId}/messages`).then((r) => setThread(r.data)).catch((e) => toast.error(errText(e)));
  }, [activeId]);
  useEffect(() => { loadThread(); }, [loadThread]);

  const deleteSelected = async () => {
    try {
      for (const id of selMsgs) {
        await api.delete(`/conversations/${activeId}/messages/${id}`);
      }
      toast.success(`Deleted ${selMsgs.length} message${selMsgs.length === 1 ? "" : "s"}`);
      setSelMsgs([]); setConfirmMulti(false);
      loadThread(); loadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  const send = async () => {
    if (!text.trim() || !activeId) return;
    try {
      await api.post(`/conversations/${activeId}/messages`, { message: text });
      setText("");
      loadThread();
      loadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  const delMsg = async (id) => {
    try {
      await api.delete(`/conversations/${activeId}/messages/${id}`);
      setConfirmDel(null);
      loadThread();
      loadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  const bulkClear = async (action) => {
    try {
      const { data } = await api.post(`/admin/conversations/${activeId}/clear`, { action });
      setConfirmBulk(null);
      if (action === "wipe") {
        toast.success(`Cleared ${data.wiped} message${data.wiped === 1 ? "" : "s"}`);
        loadThread();
        loadConvs();
      } else {
        toast.success("Conversation archived");
        setActiveId(null);
        setThread(null);
        loadConvs();
      }
    } catch (e) { toast.error(errText(e)); }
  };

  const startTeamChat = async (target_id) => {
    try {
      const { data } = await api.post(`/staff/conversations/${target_id}`);
      setShowNew(false);
      await loadConvs();
      setSegment("team");
      setActiveId(data.conversation_id);
    } catch (e) { toast.error(errText(e)); }
  };

  const switchSegment = (seg) => { setSegment(seg); setActiveId(null); setThread(null); };

  const removeConv = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      setConfirmRemove(null);
      if (activeId === id) { setActiveId(null); setThread(null); }
      loadConvs();
      toast.success("Removed from your inbox", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/conversations/${id}/restore`);
              loadConvs();
              toast.success("Conversation restored");
            } catch (e) { toast.error(errText(e)); }
          },
        },
      });
    } catch (e) { toast.error(errText(e)); }
  };

  if (!convs) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F5551E]" size={28} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1">
          <button onClick={() => switchSegment("users")} data-testid="conv-seg-users"
            className={`px-4 h-9 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${segment === "users" ? "bg-[#12234A] text-white" : "text-slate-500 hover:text-[#12234A]"}`}>
            <Users size={15} /> Users <span className={`text-xs ${segment === "users" ? "text-white/70" : "text-slate-400"}`}>({userConvs.length})</span>
          </button>
          <button onClick={() => switchSegment("team")} data-testid="conv-seg-team"
            className={`px-4 h-9 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${segment === "team" ? "bg-[#12234A] text-white" : "text-slate-500 hover:text-[#12234A]"}`}>
            <UserCog size={15} /> Moderators <span className={`text-xs ${segment === "team" ? "text-white/70" : "text-slate-400"}`}>({teamConvs.length})</span>
          </button>
        </div>
        {segment === "team" && (
          <Button onClick={() => setShowNew(true)} data-testid="new-team-chat-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5 h-9"><Plus size={16} /> New</Button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 bg-white rounded-2xl border border-slate-200/80 overflow-hidden" style={{ minHeight: 480 }}>
        <div className="border-r border-slate-100 overflow-y-auto max-h-[560px]">
          <ConvList
            items={list}
            activeId={activeId}
            onSelect={setActiveId}
            onDelete={(id) => setConfirmRemove(id)}
            emptyText={segment === "team" ? "No moderator chats yet. Tap “New” to message a teammate." : "No user conversations yet."}
          />
        </div>
        <div className="md:col-span-2 flex flex-col max-h-[560px]">
          {!activeId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2 p-8">
              <Inbox size={40} /><p className="text-sm">Select a conversation to view messages</p>
            </div>
          ) : !thread ? (
            <div className="flex-1 flex justify-center items-center"><Loader2 className="animate-spin text-[#F5551E]" size={24} /></div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <Avatar className="h-10 w-10"><AvatarImage src={photoUrl(thread.conversation.other_user.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{thread.conversation.other_user.name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#12234A] text-sm truncate">{thread.conversation.other_user.name}</p>
                  {thread.conversation.kind === "direct"
                    ? <p className="text-xs text-slate-400">Direct message</p>
                    : <Link to={`/listing/${thread.conversation.listing_id}`} className="text-xs text-slate-400 hover:text-[#F5551E]">Re: {thread.conversation.listing_title}</Link>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="conv-actions-trigger" className="text-slate-400 hover:text-[#12234A] rounded-lg p-1.5 hover:bg-slate-100 transition-colors shrink-0" title="Conversation actions"><MoreVertical size={18} /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel className="text-xs">Conversation</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {user?.role === "admin" && (
                      <>
                        <DropdownMenuItem onClick={() => setConfirmBulk("wipe")} data-testid="conv-wipe" className="text-red-600 focus:text-red-700"><Eraser size={15} className="mr-2" /> Clear all messages</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmBulk("archive")} data-testid="conv-archive"><Archive size={15} className="mr-2" /> Archive conversation</DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => setConfirmRemove(activeId)} data-testid="conv-delete-header" className="text-red-600 focus:text-red-700"><Trash2 size={15} className="mr-2" /> Delete from my inbox</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-message-thread">
                {selMsgs.length > 0 && (
                  <div className="sticky top-0 z-10 -mt-1 mb-1 flex items-center justify-between gap-2 bg-[#FFF4F0] border border-[#F5551E]/30 rounded-xl px-3 py-2" data-testid="msg-bulk-bar">
                    <span className="text-sm font-semibold text-[#12234A]">{selMsgs.length} selected</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelMsgs([])} data-testid="msg-bulk-clear" className="text-xs font-semibold text-slate-500 hover:text-[#12234A]">Clear</button>
                      <Button onClick={() => setConfirmMulti(true)} data-testid="msg-bulk-delete" className="h-8 rounded-lg bg-red-600 hover:bg-red-700 text-white gap-1.5 text-xs"><Trash2 size={13} /> Delete selected</Button>
                    </div>
                  </div>
                )}
                {thread.messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  const canDelete = !m.deleted && (!m.sender_is_staff || user?.role === "admin" || m.sender_id === user?.user_id);
                  const checked = selMsgs.includes(m.id);
                  return (
                    <div key={m.id} className={`group flex items-center gap-1.5 ${mine ? "justify-end" : "justify-start"}`} data-testid={`admin-message-${m.id}`}>
                      {canDelete && (
                        <input type="checkbox" checked={checked} onChange={() => toggleMsgSel(m.id)} data-testid={`admin-msg-check-${m.id}`} className={`h-4 w-4 rounded accent-[#F5551E] shrink-0 transition-opacity ${checked ? "opacity-100" : "opacity-0 group-hover:opacity-100"} ${mine ? "order-2" : "order-none"}`} />
                      )}
                      {mine && canDelete && (
                        <button onClick={() => setConfirmDel(m.id)} data-testid={`admin-delete-message-${m.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 shrink-0" title="Delete message"><Trash2 size={14} /></button>
                      )}
                      {m.deleted ? (
                        <div className="max-w-[75%] px-4 py-2 rounded-2xl text-sm bg-slate-100 text-slate-400 italic flex items-center gap-1.5"><Ban size={13} /> This message was deleted</div>
                      ) : (
                        <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-[#F5551E] text-white rounded-br-sm" : "bg-slate-100 text-[#12234A] rounded-bl-sm"} ${checked ? "ring-2 ring-[#F5551E] ring-offset-1" : ""}`}>{m.body}</div>
                      )}
                      {!mine && canDelete && (
                        <button onClick={() => setConfirmDel(m.id)} data-testid={`admin-delete-message-${m.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 shrink-0" title="Delete message"><Trash2 size={14} /></button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="admin-message-input" placeholder="Type a message…" className="rounded-xl" />
                <Button onClick={send} data-testid="admin-send-message-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white px-4"><Send size={16} /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="new-team-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><UserCog size={18} className="text-[#F5551E]" /> Message a teammate</DialogTitle>
            <DialogDescription className="text-slate-500">Start or continue a private chat with a moderator or admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {team.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No other staff members yet.</p>}
            {team.map((t) => (
              <button key={t.user_id} onClick={() => startTeamChat(t.user_id)} data-testid={`team-member-${t.user_id}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 text-left transition-colors">
                <Avatar className="h-9 w-9"><AvatarImage src={photoUrl(t.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{t.name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1"><p className="font-semibold text-[#12234A] text-sm truncate">{t.name}</p><p className="text-xs text-slate-400 capitalize">{t.role}</p></div>
                <Send size={15} className="text-slate-300" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="admin-delete-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Delete this message?</DialogTitle>
            <DialogDescription className="text-slate-500">It will show as "This message was deleted" for everyone in the chat. This can't be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmDel(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={() => delMsg(confirmDel)} data-testid="admin-confirm-delete-message" className="rounded-xl bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmMulti} onOpenChange={(o) => !o && setConfirmMulti(false)}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="admin-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Delete {selMsgs.length} message{selMsgs.length === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription className="text-slate-500">Each will show as "This message was deleted" for everyone in the chat. This can't be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmMulti(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={deleteSelected} data-testid="admin-confirm-bulk-delete" className="rounded-xl bg-red-600 hover:bg-red-700 text-white">Delete {selMsgs.length}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(null)}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="conv-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2">
              {confirmBulk === "wipe" ? <><Eraser size={18} className="text-red-600" /> Clear all messages?</> : <><Archive size={18} className="text-[#F5551E]" /> Archive conversation?</>}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {confirmBulk === "wipe"
                ? "Every message in this thread will show as \"This message was deleted\" for everyone. This can't be undone."
                : "This removes the conversation from your inbox. It will reappear if the user sends a new message."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmBulk(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={() => bulkClear(confirmBulk)} data-testid="conv-bulk-confirm" className={`rounded-xl text-white ${confirmBulk === "wipe" ? "bg-red-600 hover:bg-red-700" : "bg-[#F5551E] hover:bg-[#E04812]"}`}>{confirmBulk === "wipe" ? "Clear all" : "Archive"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="conv-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Delete conversation?</DialogTitle>
            <DialogDescription className="text-slate-500">This removes the conversation from your inbox. It will reappear if the user sends a new message.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmRemove(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={() => removeConv(confirmRemove)} data-testid="conv-remove-confirm" className="rounded-xl bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


function FeatureDefaultsBar() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/staff/feature-defaults").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async (next) => {
    try {
      const { data } = await api.put("/admin/feature-defaults", { days: next.days, auto_renew: next.days ? next.auto_renew : false });
      setCfg(data);
      resetFeatureDefaultsCache();
      toast.success(data.days ? `Default feature: ${data.days} days${data.auto_renew ? ", auto-renew" : ""}` : "Default feature: no end date");
    } catch (e) { toast.error(errText(e)); }
  };
  if (!cfg) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="feature-defaults-bar">
      <span className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5"><Star size={15} className="text-[#F5551E]" /> Default feature duration</span>
      <span className="text-xs text-slate-500">Pre-selected when staff feature a listing from its card.</span>
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 ml-auto" data-testid="feature-defaults-options">
        {cfg.options.map((d) => (
          <button key={d} onClick={() => save({ ...cfg, days: d })} data-testid={`feature-default-${d}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${cfg.days === d ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500 hover:text-[#12234A]"}`}>
            {d === 0 ? "No end" : `${d} days`}
          </button>
        ))}
      </div>
      <label className={`flex items-center gap-2 text-xs font-semibold ${cfg.days ? "text-[#12234A] cursor-pointer" : "text-slate-300"}`}>
        <Switch checked={!!cfg.auto_renew && !!cfg.days} disabled={!cfg.days} onCheckedChange={(v) => save({ ...cfg, auto_renew: v })} data-testid="feature-default-renew" /> Auto-renew
      </label>
    </div>
  );
}
